// PRC-043 (D6, D7 · Pablo, 04/11/2026): los planes se definen solo aquí. Los
// muestran igual la página de planes, Mi suscripción, la portada y «Prueba
// terminada». Precios sin IVA; se muestran siempre «+ IVA».
import { SUPPORT_EMAIL } from './journeyBilling';
import { formatEuro } from './formatNumber';

export type PlanId = 'trial' | 'growth' | 'autonomy';

export interface PlanFeatureGroup {
  group: string;
  items: string[];
}

export interface PlanDefinition {
  id: PlanId;
  name: string;
  tagline: string;
  // Precio en euros (sin IVA); null si no se vende online.
  monthly: number | null;
  yearly: number | null;
  // Contenido de la tarjeta (bocetos G8 y G9).
  groups: PlanFeatureGroup[];
  // Lo mismo en una lista plana (Mi suscripción · «Qué incluye»).
  features: string[];
  comingSoon?: boolean;
  // Nota bajo el botón.
  footnote?: string;
}

export const PLAN_LIMITS = {
  users: 5,
  salesLines: 500_000,
  batchRowsPerMonth: 50_000,
  trialOptimizations: 1_000,
  trialDays: 30,
} as const;

// PRC-010 (D5): métodos de pago de Growth.
export const PAYMENT_METHODS_LABEL = 'Tarjeta o domiciliación SEPA';

export const AUTONOMY_CONTACT_HREF = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Autonomy')}`;

const flat = (groups: PlanFeatureGroup[]) => groups.flatMap((g) => g.items);

const GROWTH_GROUPS: PlanFeatureGroup[] = [
  { group: 'Margin Center', items: ['Overview, Explorer y fichas de cliente y producto', 'Control comercial y consulta comercial en lenguaje natural'] },
  { group: 'Precios', items: ['Evaluación rápida con contexto e historial', 'Optimizaciones ilimitadas · lotes hasta 50.000 filas al mes'] },
  { group: 'Datos', items: ['CSV o Excel, y carga automática cada noche desde Odoo o PostgreSQL', 'Hasta 500.000 líneas de venta', 'Modelos reentrenados cada semana con tus datos nuevos'] },
  { group: 'Equipo y soporte', items: ['5 usuarios', 'Puesta en marcha: sesión de 60 min y primera carga', 'Soporte por email en 1 día laborable'] },
];

const AUTONOMY_GROUPS: PlanFeatureGroup[] = [
  { group: 'Todo Growth', items: ['Planificación: previsión de demanda y compras', 'Conexión directa con tu ERP', 'Acompañamiento dedicado'] },
];

const TRIAL_GROUPS: PlanFeatureGroup[] = [
  { group: 'Todo lo de Growth durante 30 días', items: ['Hasta 1.000 optimizaciones', 'Tus datos se guardan si luego contratas', 'Hasta 5 usuarios, como en Growth'] },
];

export const PLANS: Record<PlanId, PlanDefinition> = {
  trial: {
    id: 'trial',
    name: 'Prueba',
    tagline: '30 días',
    monthly: 0,
    yearly: 0,
    groups: TRIAL_GROUPS,
    features: flat(TRIAL_GROUPS),
    footnote: 'Nada se cobra solo al terminar',
  },
  growth: {
    id: 'growth',
    name: 'Growth',
    tagline: 'Para empezar a ganar margen',
    monthly: 990,
    yearly: 9_900,
    groups: GROWTH_GROUPS,
    features: flat(GROWTH_GROUPS),
  },
  autonomy: {
    id: 'autonomy',
    name: 'Autonomy',
    tagline: 'Para equipos que también planifican la demanda',
    monthly: null,
    yearly: null,
    comingSoon: true,
    groups: AUTONOMY_GROUPS,
    features: ['Todo Growth', ...flat(AUTONOMY_GROUPS)],
    footnote: 'Sin compra online. Te contamos fechas y condiciones en una llamada.',
  },
};

// Tabla «Comparar planes» (boceto G8). ✓ = incluido, — = no incluido.
export const PLAN_COMPARISON: Array<{ group: string; rows: Array<{ label: string; growth: string; autonomy: string }> }> = [
  { group: 'Margin Center', rows: [
    { label: 'Overview, Explorer y fichas', growth: '✓', autonomy: '✓' },
    { label: 'Control comercial y consulta comercial', growth: '✓', autonomy: '✓' },
  ] },
  { group: 'Precios', rows: [
    { label: 'Evaluación rápida e historial', growth: '✓', autonomy: '✓' },
    { label: 'Lotes', growth: '50.000 filas/mes', autonomy: 'A medida' },
  ] },
  { group: 'Datos', rows: [
    { label: 'CSV y Excel', growth: '✓', autonomy: '✓' },
    { label: 'Carga automática (Odoo, PostgreSQL)', growth: 'Cada noche', autonomy: 'Conexión directa con el ERP' },
    { label: 'Líneas de venta', growth: '500.000', autonomy: 'A medida' },
    { label: 'Reentrenamiento de modelos', growth: 'Semanal', autonomy: 'Semanal' },
  ] },
  { group: 'Planificación', rows: [
    { label: 'Previsión de demanda y compras', growth: '—', autonomy: '✓' },
  ] },
  { group: 'Equipo y soporte', rows: [
    { label: 'Usuarios', growth: '5', autonomy: 'A medida' },
    { label: 'Soporte', growth: 'Email · 1 día laborable', autonomy: 'Acompañamiento dedicado' },
  ] },
];

// Punto de miles también con cuatro cifras («9.900 €»): es-ES no agrupa por debajo de 10.000.
// PRC-046: formato común de Pricing (sin decimales en los precios de plan).
export const formatEuros = (value: number) => formatEuro(value, 0);
